import React from "react";
import { TextField, FormControl, InputLabel } from "@mui/material";
import { Formik, Form } from "formik";
import dayjs from "dayjs";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import FormDatePicker from "components/FormFields/FormDatePicker";
import { useMarkTaskCompleteMutation } from "app/store/liveCallTasks/api/liveCallTasksApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { liveCallManualTaskCompleteSchema } from "schemas/liveCallManualTaskCompleteSchema";

const MarkTaskCompleteModal = ({
  taskId,
  show,
  onCancel,
  onSubmit = () => {},
}) => {
  const [markTaskComplete] = useMarkTaskCompleteMutation();

  const handleComplete = async (values, { setSubmitting }) => {
    if (!taskId) {
      return;
    }

    try {
      const response = await markTaskComplete({
        taskId,
        reason: values.reason.trim(),
        completionDate: values.completionDate,
      }).unwrap();

      if (!response?.error) {
        showSuccessToast("Task marked as complete.");
        onCancel();
        onSubmit();
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Formik reads initialValues only on mount, and the parent unmounts this
  // modal on close, so each open starts fresh with the current date.
  return (
    <Formik
      initialValues={{ completionDate: dayjs(), reason: "" }}
      validationSchema={liveCallManualTaskCompleteSchema}
      onSubmit={handleComplete}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        handleSubmit,
        isSubmitting,
      }) => (
        <Form onSubmit={handleSubmit}>
          <ConfirmModal
            title="Add follow-up note"
            show={show}
            showCloseIcon
            isConfirming={isSubmitting}
            onConfirm={handleSubmit}
            onCancel={onCancel}
            confirmText="Complete task"
            cancelText="Cancel"
            confirmButtonProps={{
              color: "secondary",
              disabled: !values.reason.trim(),
            }}
            cancelButtonProps={{
              variant: "outlined",
              color: "secondary",
            }}
          >
            <FormControl fullWidth margin="none" sx={{ py: 2.5 }}>
              <FormDatePicker
                disabled
                name="completionDate"
                label="Date"
                value={dayjs(values.completionDate)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "none",
                  },
                }}
              />
            </FormControl>

            <InputLabel sx={{ color: "#757575", mt: 2, mb: 1 }}>
              Add follow-up notes
            </InputLabel>
            <TextField
              name="reason"
              placeholder="Add Follow-up notes"
              fullWidth
              margin="none"
              multiline
              rows={8}
              value={values.reason}
              onChange={handleChange}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                setFieldTouched("reason", true);
                setFieldValue("reason", trimmed);
              }}
              error={touched.reason && Boolean(errors.reason)}
              helperText={touched.reason && errors.reason}
              inputProps={{ maxLength: 500 }}
            />
          </ConfirmModal>
        </Form>
      )}
    </Formik>
  );
};

export default MarkTaskCompleteModal;
